import React, {useRef, useState, useEffect} from 'react';
import {
  Box,
  Flex,
  Button,
  Heading,
  Divider,
  Spacer,
  FormLabel,
  Progress,
  Text,
} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import BoxCard from '../../components/General/BoxCard';
import {DatePicker} from '../../components/General/FormInputs';
import LinkButton from '../../components/General/LinkButton';
import ServiceTypeList from '../../components/General/ServiceTypeList';
import {queryStringBuilder, get} from '../../service/ApiService';
import {Export, Client} from '../../service/Urls';
import ErrorHelper from '../../service/ErrorHelper';
import {getCodeByKey} from '../../helpers/ServiceTypeDictionary';
import LoadingWheel from '../../components/General/LoadingWheel';
import Select from 'react-select';
import {useQuery} from 'react-query';
import * as XLSX from 'xlsx';

const SurveyExport = () => {
  const [toast, setToast] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [fileType, setFileType] = useState('csv'); // 'csv' or 'xlsx'
  const [isLoading, setIsLoading] = useState(false);
  const linkRef = useRef(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    currentClientName: '',
  });

  const {
    register,
    handleSubmit,
    control,
    formState: {errors},
  } = useForm();

  const {data: clientData, isLoading: isLoadingClients} = useQuery(
    ['client', 'clientList'],
    () => get(Client.LIST + '?PageSize=1000'),
    {
      onError: () => {
        setToast({error: 'Error loading clients'});
      },
    }
  );

  const clientOptions = [
    {value: 'all', label: 'All'},
    ...(clientData?.data?.map(client => ({
      value: client?.id,
      label: `${client?.clId} | ${client?.clCname ?? ''}`,
    })) ?? []),
  ];

  useEffect(() => {
    if (fileUrl) {
      linkRef.current?.click();
      // Clean up the object URL after download
      const currentUrl = fileUrl;
      setTimeout(() => {
        window.URL.revokeObjectURL(currentUrl);
        setFileUrl('');
      }, 100);
    }
  }, [fileUrl]);

  const filledCheck = () => {
    const formEl = document.forms.SurveyExport;
    const formData = new FormData(formEl);
    const sDate = formData.get('startDate');
    const eDate = formData.get('endDate');

    // Check if all required fields are filled: dates and a client selected
    if (sDate != null && eDate != null && selectedClient) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  useEffect(() => {
    filledCheck();
  }, [selectedClient]);

  // Keep Group immediately before Arranger, even if the API omits a null group on some rows.
  const getOrderedHeaders = (data) => {
    const headers = [];
    data.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!headers.includes(key)) {
          headers.push(key);
        }
      });
    });

    const withoutGroup = headers.filter(key => key !== 'group');
    const arrangerIndex = withoutGroup.indexOf('arranger');
    if (arrangerIndex >= 0) {
      withoutGroup.splice(arrangerIndex, 0, 'group');
      return withoutGroup;
    }

    const locationIndex = withoutGroup.indexOf('location');
    if (locationIndex >= 0) {
      withoutGroup.splice(locationIndex + 1, 0, 'group');
      return withoutGroup;
    }

    return ['group', ...withoutGroup];
  };

  // Helper function to convert JSON data to CSV format
  const convertToCSV = (data) => {
    if (!data || data.length === 0) {
      return '';
    }

    const headers = getOrderedHeaders(data);
    
    // Create CSV header row
    const csvHeaders = headers.map(header => {
      // Escape commas and quotes in header names
      const escapedHeader = String(header).replace(/"/g, '""');
      return `"${escapedHeader}"`;
    }).join(',');

    // Create CSV data rows
    const csvRows = data.map(row => {
      return headers.map(header => {
        let value = row[header];
        
        // Handle null/undefined values
        if (value === null || value === undefined) {
          value = '';
        } else {
          value = String(value);
        }
        
        // Escape quotes and handle newlines
        value = value.replace(/"/g, '""');
        
        // Wrap in quotes if value contains comma, quote, or newline
        if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
          return `"${value}"`;
        }
        
        return value;
      }).join(',');
    });

    // Combine headers and rows
    return [csvHeaders, ...csvRows].join('\n');
  };

  // Helper function to convert JSON data to Excel format
  const convertToExcel = (data) => {
    if (!data || data.length === 0) {
      return null;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const headers = getOrderedHeaders(data);

    // Convert JSON data to worksheet with Group immediately before Arranger
    const ws = XLSX.utils.json_to_sheet(data, {header: headers});
    
    // Set column widths for better readability
    const maxWidth = 50;
    const wscols = headers.map(() => ({wch: maxWidth}));
    ws['!cols'] = wscols;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Surveys');
    
    // Write workbook to binary string
    return XLSX.write(wb, {type: 'binary', bookType: 'xlsx'});
  };

  // Helper function to convert binary string to blob
  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  };

  async function ExportSurvey(exportType = 'csv') {
    // Validate that a client is selected
    if (!selectedClient) {
      setToast({response: {error: true}, errorMessage: 'Please select a client'});
      return;
    }

    setIsLoading(true);
    const submit = handleSubmit(async data => {
      const isAllClients = selectedClient.value === 'all';
      const clientName = selectedClient.label || 'Selected Client';

      const params = {
        startDate: data.startDate,
        endDate: data.endDate,
        CsvcType: getCodeByKey(data.ServiceType),
      };

      // If "All" is selected, use clients=all, otherwise use the client ID
      if (isAllClients) {
        params.clients = 'all';
      } else {
        params.clients = [selectedClient.value];
      }

      // Initialize progress
      setProgress({
        current: 0,
        total: 1,
        percentage: 0,
        currentClientName: clientName,
      });

      // Build the URL for exporting surveys - use different endpoint for "all"
      const endpoint = isAllClients ? Export.GET_SURVEYS_ALL : Export.GET_SURVEYS;
      const url = queryStringBuilder(endpoint, params);
      
      try {
        // Update progress to show we're fetching
        setProgress({
          current: 1,
          total: 1,
          percentage: 50,
          currentClientName: clientName,
        });

        const response = await get(url);

        setIsLoading(false);

        if (response.error) {
          setToast({
            response: {error: true},
            errorMessage: `Error fetching data for ${clientName}: ${response.error}`,
          });
          setProgress({
            current: 0,
            total: 0,
            percentage: 0,
            currentClientName: '',
          });
          return;
        }

        if (!response.isSuccess || !response.data || !Array.isArray(response.data)) {
          setToast({
            response: {error: true},
            errorMessage: 'No data found for the selected client and date range',
          });
          setProgress({
            current: 0,
            total: 0,
            percentage: 0,
            currentClientName: '',
          });
          return;
        }

        const allData = response.data;

        // Update progress to show we're creating the file
        setProgress({
          current: 1,
          total: 1,
          percentage: 100,
          currentClientName: clientName,
        });

        // If we have data, create the export file
        if (allData.length > 0) {
          let blob;
          
          if (exportType === 'xlsx') {
            // Convert JSON data to Excel format
            const excelBinary = convertToExcel(allData);
            if (excelBinary) {
              const excelBuffer = s2ab(excelBinary);
              blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              setFileType('xlsx');
            } else {
              setToast({response: {error: true}, errorMessage: 'No data to export'});
              setProgress({
                current: 0,
                total: 0,
                percentage: 0,
                currentClientName: '',
              });
              return;
            }
          } else {
            // Convert JSON data to CSV format
            const csvContent = convertToCSV(allData);
            
            // Create a properly typed blob for CSV files
            // Add BOM for UTF-8 to ensure Excel opens it correctly
            const BOM = '\uFEFF';
            blob = new Blob([BOM + csvContent], {
              type: 'text/csv;charset=utf-8;',
            });
            setFileType('csv');
          }
          
          const fileURL = window.URL.createObjectURL(blob);
          setFileUrl(fileURL);
        } else {
          setToast({
            response: {error: true},
            errorMessage: 'No data found for the selected client and date range',
          });
        }

        // Reset progress
        setProgress({
          current: 0,
          total: 0,
          percentage: 0,
          currentClientName: '',
        });
      } catch (error) {
        setIsLoading(false);
        setToast({
          response: {error: true},
          errorMessage: `Error fetching data for ${clientName}: ${error.message}`,
        });
        setProgress({
          current: 0,
          total: 0,
          percentage: 0,
          currentClientName: '',
        });
      }
    });
    await submit();
  }

  return (
    <form id="SurveyExport" onChange={filledCheck}>
      <Flex flexDir={{base: 'column', lg: 'row'}}>
        {/* Survey Export */}
        <Box w="75%" mr="3" alignContent="center">
          <BoxCard>
            <Heading>Export Survey</Heading>
            <Divider my="3" />

            <Flex>
              <DatePicker id="startDate" name="Starting Date" register={register} errors={errors} required mx="3" />
              <DatePicker id="endDate" name="Ending Date" register={register} errors={errors} required mx="3" />
            </Flex>
            <Divider my="3" />
            <Flex alignItems="center" mb="3">
              <FormLabel fontSize="sm" mb="0" mr="3" minW="120px" requiredIndicator>
                Select Client
              </FormLabel>
              <Box flex="1" maxW="400px">
                <Select
                  options={clientOptions}
                  onChange={value => {
                    setSelectedClient(value);
                  }}
                  value={selectedClient}
                  isDisabled={clientOptions.length <= 0 || isLoadingClients}
                  placeholder="Select a client..."
                  isClearable={true}
                />
              </Box>
            </Flex>
            
            <Divider my="3" />
            <Box>
              <Heading size="md" mb="3" textAlign="start">
                Survey type
              </Heading>
              <ServiceTypeList control={control} filters={['Funeral', 'Cremation']} />
            </Box>
            <Divider my="3" />
            
            {isLoading && progress.total > 0 && (
              <Box mb="4">
                <Flex justifyContent="space-between" mb="2">
                  <Text fontSize="sm" fontWeight="medium">
                    {progress.percentage < 100 ? `Fetching data for ${progress.currentClientName}...` : `Creating export file...`}
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {progress.percentage}%
                  </Text>
                </Flex>
                <Progress value={progress.percentage} colorScheme="blue" size="lg" borderRadius="md" />
              </Box>
            )}
            <Flex>
              {isLoading ? (
                <LoadingWheel />
              ) : (
                <>
                  <Box w="10%" margin="0.5">
                    <LinkButton id="formButton" to="/surveys" register={register} label="Back" />
                  </Box>
                  <Button colorScheme="secondary" mx="3" onClick={() => ExportSurvey('csv')} disabled={isDisabled}>
                    Export Survey CSV
                  </Button>
                  <Button colorScheme="secondary" mx="3" onClick={() => ExportSurvey('xlsx')} disabled={isDisabled}>
                    Export Survey Excel
                  </Button>
                </>
              )}
              <Spacer />
            </Flex>
          </BoxCard>
          <a
            href={fileUrl}
            download={fileType === 'xlsx' ? 'surveys.xlsx' : 'surveys.csv'}
            ref={linkRef}
            style={{display: 'none'}}
          />
        </Box>
      </Flex>
      <ErrorHelper toastResponse={toast} />
    </form>
  );
};

export default SurveyExport;

